import {
  LeaderboardResultPage,
  LeaderboardStats,
  OwnerRankings,
} from '~/../api/common.types';
import CheckboxList from '~/components/common/CheckboxList';
import Tabs from '~/components/common/Tabs';
import Levels from '~/components/hero/Levels';
import LeaderboardTable from '~/components/leaderboard/LeaderboardTable';
import { FILTERS } from '~/constants';
import { useGetWarriors } from '~/hooks/useGetWarriors';

function WarriorsItems({
  warrior,
  filterType,
  filterState,
  handleFilterClick,
}) {
  const warriorName = warrior.value.toLowerCase();
  const isActive = filterState.includes(warriorName);
  return (
    <div
      className={`checkbox-button checkbox-button--image ${
        isActive ? 'checkbox-button--active' : ''
      }`}
      key={warrior.id}
    >
      <img
        src={isActive ? warrior.animation_url : warrior.image}
        alt={warrior.name}
        className="warrior__img"
      />
      <img
        src={warrior.animation_url}
        alt={warrior.name}
        className="warrior__img warrior__img--hover"
      />
      <input
        type="checkbox"
        name={warrior.name}
        id={warrior.name}
        readOnly={true}
        checked={isActive}
        value={warriorName}
        onClick={() => handleFilterClick({ value: warriorName, filterType })}
      />
      <label htmlFor={warrior.name}>{warrior.name}</label>
    </div>
  );
}

export function LeaderBoard({
  page,
  filterStates,
  setFilters,
  statTotals,
  ownedWarriorRankings,
}: {
  page: LeaderboardResultPage | undefined;
  filterStates: any;
  setFilters: any;
  statTotals: LeaderboardStats | undefined;
  ownedWarriorRankings: OwnerRankings[] | undefined;
}) {
  const { data: warriors } = useGetWarriors();
  const handleFilterClick = ({ value, filterType }) => {
    const filterState = filterStates[filterType];
    const setFilterHook = setFilters[filterType];
    if (filterState.includes(value)) {
      setFilterHook((filterState) =>
        filterState.filter((val) => val !== value)
      );
    } else {
      setFilterHook([...filterState, value]);
    }
  };

  const ownedWarriorIDs = ownedWarriorRankings?.map(
    (warrior) => warrior.warrior_id
  );
  const handleOwnedWarriorToggle = (value: boolean) => {
    const setFilterHook = setFilters[FILTERS.owned];
    if (value) {
      if (ownedWarriorIDs && ownedWarriorIDs.length) {
        setFilterHook([ownedWarriorIDs]);
      } else {
        // handle user not owning any warriors
        setFilterHook([-1]);
      }
    } else {
      setFilterHook([]);
    }
  };

  // const handleTournamentActive = (value: boolean) => {
  //   const setFilterHook = setFilters[FILTERS.context];
  //   if (value) {
  //     setFilterHook(true);
  //   } else {
  //     setFilterHook(false);
  //   }
  // };

  return (
    <>
      <Levels />
      <div className="filters">
        <p className="filters__title">Filter your search</p>
        <div className="warriors">
          <p className="filters__subtitle">Choose the warrior</p>
          <div className="warriors-list">
            {warriors?.map((warrior, index) => (
              <WarriorsItems
                warrior={warrior}
                filterType={FILTERS.characters}
                filterState={filterStates[FILTERS.characters]}
                handleFilterClick={handleFilterClick}
                key={index}
              />
            ))}{' '}
          </div>
        </div>
        <div className="checkbox-filter">
          <CheckboxList
            title={'Level'}
            col={'2'}
            filterType={FILTERS.levels}
            filterState={filterStates[FILTERS.levels]}
            handleFilterClick={handleFilterClick}
            items={[
              { label: 'One', value: 1, total: statTotals?.levelStats[1] },
              { label: 'Two', value: 2, total: statTotals?.levelStats[2] },
              { label: 'Three', value: 3, total: statTotals?.levelStats[3] },
              { label: 'Four', value: 4, total: statTotals?.levelStats[4] },
              { label: 'Five', value: 5, total: statTotals?.levelStats[5] },
              {
                label: 'Dead',
                value: 'dead',
                total: statTotals?.levelStats['dead'],
              },
            ]}
          />
          <CheckboxList
            title={'Syndicate'}
            col={'3'}
            filterType={FILTERS.syndicates}
            filterState={filterStates[FILTERS.syndicates]}
            handleFilterClick={handleFilterClick}
            items={[
              {
                label: 'Metal',
                value: 'metal',
                total: statTotals?.syndicateStats.metal,
              },
              {
                label: 'Fire',
                value: 'fire',
                total: statTotals?.syndicateStats.fire,
              },
              {
                label: 'Timber',
                value: 'timber',
                total: statTotals?.syndicateStats.timber,
              },
              {
                label: 'Water',
                value: 'water',
                total: statTotals?.syndicateStats.water,
              },
              {
                label: 'Earth',
                value: 'earth',
                total: statTotals?.syndicateStats.earth,
              },
              {
                label: 'Spirit',
                value: 'spirit',
                total: statTotals?.syndicateStats.spirit,
              },
              {
                label: 'Renegade',
                value: 'renegade',
                total: statTotals?.syndicateStats.renegade,
              },
            ]}
          />
          <CheckboxList
            title={'Type'}
            col={'1'}
            filterType={FILTERS.types}
            filterState={filterStates[FILTERS.types]}
            handleFilterClick={handleFilterClick}
            items={[
              {
                label: 'Humans',
                value: 'human',
                total: statTotals?.typeStats.human,
              },
              {
                label: 'Zombies',
                value: 'zombie',
                total: statTotals?.typeStats.zombie,
              },
              {
                label: 'Aliens',
                value: 'alien',
                total: statTotals?.typeStats.alien,
              },
            ]}
          />
          <CheckboxList
            title={'HP'}
            col={'2'}
            filterType={FILTERS.hps}
            filterState={filterStates[FILTERS.hps]}
            handleFilterClick={handleFilterClick}
            items={[
              { label: '0-50', value: 0, total: statTotals?.hpStats[0] },
              { label: '51-75', value: 51, total: statTotals?.hpStats[51] },
              { label: '76-100', value: 76, total: statTotals?.hpStats[76] },
              { label: '101-125', value: 101, total: statTotals?.hpStats[101] },
              { label: '126-149', value: 126, total: statTotals?.hpStats[126] },
              { label: '150+', value: 150, total: statTotals?.hpStats[150] },
            ]}
          />
        </div>
      </div>
      <div className="checkbox-filter">
        <Tabs
          items={[
            { label: 'All warriors', value: false },
            { label: 'Your warriors', value: true },
          ]}
          handleFilterClick={handleOwnedWarriorToggle}
        />
        {/* <Tabs
          items={[
            { label: 'All Warriors', value: false },
            { label: 'Tournament Active', value: true },
          ]}
          handleFilterClick={handleTournamentActive}
        /> */}
      </div>
      <LeaderboardTable
        warriors={page?.results}
        deadOnly={filterStates[FILTERS.levels].includes('-1')}
        ownedWarriorIDs={ownedWarriorIDs}
        // tournamentParticipants={statTotals?.contexts}
      />
    </>
  );
}
